import { createContext, useContext, useMemo, type ReactNode } from "react";
import { OPERATOR_ROLES, type OperatorRole } from "@oathera/contract/rbac";

/**
 * The operator's role, carried so later screens can hide or disable controls the role cannot use,
 * with a reason — "the server enforces regardless" (design 12.3; FR-12).
 *
 * DECISION POINT (flagged to the orchestrator): the contract has NO identity endpoint. The BFF
 * resolves tenant and role from the verified Access claims and stamps them into the internal
 * envelope (src/worker/identity.ts), but it does not expose them to the SPA, and `openapi.yaml`
 * defines no `/me`, `/session` or `whoami` operation. So the shell CANNOT learn the role from the
 * contract today. Rather than invent a backend surface, this context starts in an honest `unknown`
 * state and leaves a single, obvious bootstrap seam (`roleSource`) for a later task to wire once an
 * identity operation is added to the contract (or the BFF echoes the role on a header/bootstrap
 * document). Until then the shell treats the role as unknown and, per least privilege, lets screens
 * fall back to the most restrictive behaviour; the server is the real authority regardless.
 *
 * `@oathera/contract/rbac` is the single source of truth for the role ladder (viewer < operator <
 * admin). `can(role, operationId)` there answers "may this role perform this operation"; screens
 * import it directly. This context only carries WHICH role the operator has (or that it is unknown).
 */

/** The resolved operator role, or `unknown` while the shell has no trustworthy source for it. */
export type RoleState = OperatorRole | "unknown";

export interface IdentityState {
  /** The operator's role, or `unknown`. */
  readonly role: RoleState;
  /** The resolved tenant, if the shell knows it (see the tenant context); null when unknown. */
  readonly tenant: string | null;
}

const IdentityContext = createContext<IdentityState | null>(null);

/**
 * The bootstrap seam. A later task replaces this with a real resolution (a contract identity
 * operation, or a bootstrap document the BFF serves). It returns `unknown` today — deliberately not
 * a fabricated role — so nothing in the shell assumes a privilege the operator may not have.
 */
export function bootstrapIdentity(): IdentityState {
  // TODO(task: identity bootstrap): resolve the real role once the contract exposes it. The BFF
  // already knows it from the verified Access claims (src/worker/identity.ts); surfacing it needs a
  // contract addition, which is out of scope for the shell. Keep `unknown` until then.
  return { role: "unknown", tenant: null };
}

export function IdentityProvider(props: {
  children: ReactNode;
  /** Allows a test or a later bootstrap task to inject a resolved identity. */
  value?: IdentityState;
}): ReactNode {
  const resolved = props.value ?? bootstrapIdentity();
  const value = useMemo<IdentityState>(() => resolved, [resolved]);
  return <IdentityContext.Provider value={value}>{props.children}</IdentityContext.Provider>;
}

export function useIdentity(): IdentityState {
  const ctx = useContext(IdentityContext);
  if (ctx === null) throw new Error("useIdentity must be used within <IdentityProvider>");
  return ctx;
}

/**
 * True only when the role is KNOWN to be at least `required`. An `unknown` role returns false
 * (least privilege): a control gated on this stays disabled until the role is resolved, and the
 * server enforces regardless. The role ladder comes from `@oathera/contract/rbac`.
 */
export function roleAtLeast(role: RoleState, required: OperatorRole): boolean {
  if (role === "unknown") return false;
  const rank = (r: OperatorRole): number => OPERATOR_ROLES.indexOf(r);
  return rank(role) >= rank(required);
}
